import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import { useMatrixClient } from '../../../../context/MatrixContext';
import { useUserPresence } from '../../../../hooks/useUserPresence';
import { useI18n } from '../../../../context/I18nContext';
import { generateInitialsAvatar } from '../../../../utils/avatar';
import { getPresenceColor, getPresenceTitle } from '../../../../utils/presenceUtils';
import { useDisambiguation } from '../../../../context/DisambiguationContext';
import { REACTION_FILTER_TYPES } from '../../../../constants';
import RightSidebar from '../../../RightSidebar/RightSidebar';
import './ReactionDetails.css';

const ReactionDetails = ({ 
  onClose = () => {},
  onContactClick = () => {},
  allReactions = new Map(), // All reactions for this message
  members = [], // Contact list from room context
}) => {
  const client = useMatrixClient();
  const { getPresence, isOnline } = useUserPresence();
  const { t } = useI18n();
  const { needsDisambiguation } = useDisambiguation();
  const [selectedFilter, setSelectedFilter] = useState(REACTION_FILTER_TYPES.ALL);
  
  // Get current user ID
  const currentUserId = client?.getUserId();

  // Create a lookup map for quick member access by user ID
  const memberLookup = useMemo(() => {
    const lookup = new Map();
    members.forEach(member => {
      // Members might have userId, id, or senderId as the key
      const userId = member.userId || member.id || member.senderId;
      if (userId) {
        lookup.set(userId, member);
      }
    });
    return lookup;
  }, [members]);

  // Process all reactions to get unique emojis and total count
  const reactionStats = useMemo(() => {
    const stats = {
      total: 0,
      emojis: new Map(), // emoji -> count
      allReactors: [], // All reactors with their emojis and member info
    };

    allReactions.forEach((reactorList, emojiKey) => {
      stats.emojis.set(emojiKey, reactorList.length);
      stats.total += reactorList.length;
      
      // Add each reactor with their emoji and member info
      reactorList.forEach(reactor => {
        const memberInfo = memberLookup.get(reactor.senderId);
        
        stats.allReactors.push({
          ...reactor,
          emoji: emojiKey,
          // Use member info if available, otherwise fallback to reactor data
          cleanName: memberInfo?.name || reactor.senderName?.split(':')[0].replace('@', '') || reactor.senderId.split(':')[0].replace('@', ''),
          avatarUrl: memberInfo?.avatar || reactor.senderAvatar || generateInitialsAvatar(memberInfo?.name || reactor.senderId),
        });
      });
    });

    return stats;
  }, [allReactions, memberLookup]);

  // Filter reactors based on selected filter
  const filteredReactors = useMemo(() => {
    let reactors;
    if (selectedFilter === REACTION_FILTER_TYPES.ALL) {
      reactors = reactionStats.allReactors;
    } else {
      reactors = reactionStats.allReactors.filter(reactor => reactor.emoji === selectedFilter);
    }
    
    // Sort so current user appears first (spread to avoid mutating original array)
    return [...reactors].sort((a, b) => {
      const aIsCurrentUser = a.senderId === currentUserId;
      const bIsCurrentUser = b.senderId === currentUserId;
      
      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
      return 0; // Keep original order for others
    });
  }, [reactionStats.allReactors, selectedFilter, currentUserId]);

  return (
    <RightSidebar
      isOpen={true}
      onClose={onClose}
      title={t('ReactionsDetailsView.title')}
      className="reaction-details"
    >
      <div className="reaction-details-filters">
        <button
          className={`reaction-filter-button ${selectedFilter === REACTION_FILTER_TYPES.ALL ? 'active' : ''}`}
          onClick={() => setSelectedFilter(REACTION_FILTER_TYPES.ALL)}
        >
          {t('ReactionsDetailsView.all')} {reactionStats.total}
        </button>
        {Array.from(reactionStats.emojis.entries()).map(([emojiKey, count]) => (
          <button
            key={emojiKey}
            className={`reaction-filter-button ${selectedFilter === emojiKey ? 'active' : ''}`}
            onClick={() => setSelectedFilter(emojiKey)}
          >
            {emojiKey} {count}
          </button>
        ))}
      </div>
      
      <div className="reaction-details-content">
        <div className="reaction-details-list">
          {filteredReactors.length > 0 ? (
            filteredReactors.map((reactor, index) => (
              <div key={`${reactor.senderId}-${reactor.emoji}-${index}`} className="reaction-details-item" onClick={() => onContactClick(reactor.senderId)}>
                <div className="reaction-details-avatar">
                  <img 
                    src={reactor.avatarUrl} 
                    alt={reactor.cleanName.charAt(0).toUpperCase()}
                    onError={(e) => {
                      // Fallback to initials avatar if image fails to load
                      e.target.src = generateInitialsAvatar(reactor.cleanName);
                    }}
                  />
                  <div 
                    className="reaction-details-presence-indicator" 
                    style={{ backgroundColor: getPresenceColor(getPresence(reactor.senderId), isOnline(reactor.senderId)) }}
                    title={getPresenceTitle(getPresence(reactor.senderId), isOnline(reactor.senderId))}
                  />
                </div>
                <div className="reaction-details-name">
                  <div className="reaction-details-name-row">
                    <span className="reaction-details-name-text">{reactor.cleanName}</span>
                    {reactor.senderId === currentUserId && (
                      <span className="reaction-details-you-label">{t('ReactionsDetailsView.selfLabel')}</span>
                    )}
                  </div>
                  {reactor.senderId !== currentUserId && needsDisambiguation(reactor.senderId) && (
                    <div className="reaction-details-mxid">{reactor.senderId}</div>
                  )}
                </div>
                <div className="reaction-details-emoji">
                  {reactor.emoji}
                </div>
              </div>
            ))
          ) : (
            <div className="reaction-details-empty">
              {t('ReactionsDetailsView.noReactionsFound')}
            </div>
          )}
        </div>
      </div>
    </RightSidebar>
  );
};

ReactionDetails.propTypes = {
  onClose: PropTypes.func,
  onContactClick: PropTypes.func,
  allReactions: PropTypes.instanceOf(Map),
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userId: PropTypes.string,
    senderId: PropTypes.string,
    name: PropTypes.string,
    avatar: PropTypes.string,
  })),
};

export default ReactionDetails;